/**
 * FormRenderer Component
 *
 * Renders a form for respondents to fill out.
 * Handles validation and submission.
 * Supports RTL/LTR text direction and media response types.
 */

import React, { useState, useCallback } from 'react'
import QuestionField from './QuestionField'
import { validatePhoneNumber } from '../../constants/countryCodes'
import './FormRenderer.css'

// Layout element types that are not questions
const LAYOUT_TYPES = ['section_header', 'divider']

export default function FormRenderer({
  form,
  onSubmit,
  submitting,
  surveyId,
  responseId,
  uploadToken,
  textDirection = 'ltr',
  allowedResponseTypes = ['text'],
  brandImageUrl = null,
}) {
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Update answer for a question
  const handleAnswerChange = useCallback(
    (questionId, value) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: value,
      }))
      // Clear error when user starts typing
      if (errors[questionId]) {
        setErrors((prev) => {
          const next = { ...prev }
          delete next[questionId]
          return next
        })
      }
    },
    [errors]
  )

  // Validate a single field on blur (currently phone fields only)
  const handleFieldBlur = useCallback(
    (questionId) => {
      const question = (form.questions || []).find((q) => q.id === questionId)
      if (!question) return

      if (question.type === 'phone') {
        const answer = answers[questionId]
        if (answer?.number) {
          const result = validatePhoneNumber(answer.countryCode, answer.number)
          if (!result.valid) {
            setErrors((prev) => ({ ...prev, [questionId]: result.message }))
          } else {
            setErrors((prev) => {
              const next = { ...prev }
              delete next[questionId]
              return next
            })
          }
        }
      }
    },
    [form.questions, answers]
  )

  // Validate form
  const validate = useCallback(() => {
    const newErrors = {}

    for (const question of form.questions || []) {
      // Skip layout elements — they don't collect answers
      if (LAYOUT_TYPES.includes(question.type)) continue

      const answer = answers[question.id]

      // Required validation
      if (question.required) {
        // Phone required: check that number is provided
        if (question.type === 'phone') {
          if (!answer || !answer.number || !answer.number.trim()) {
            newErrors[question.id] = 'Phone number is required'
            continue
          }
        } else if (answer === undefined || answer === null || answer === '') {
          newErrors[question.id] = 'This field is required'
          continue
        } else if (Array.isArray(answer) && answer.length === 0) {
          newErrors[question.id] = 'Please select at least one option'
          continue
        }
        // Media answer validation - check for media_url
        if (['audio', 'video', 'image'].includes(question.type)) {
          if (!answer?.media_url) {
            newErrors[question.id] = 'Please capture or upload media for this question'
            continue
          }
        }
        // Open response validation - need text or media_url depending on chosen mode
        if (question.type === 'open_response') {
          const mode = answer?.mode || 'text'
          if (mode === 'text' && (!answer?.text || !answer.text.trim())) {
            newErrors[question.id] = 'Please enter your response'
            continue
          }
          if ((mode === 'audio' || mode === 'image') && !answer?.media_url) {
            newErrors[question.id] = `Please capture or upload ${mode} for this question`
            continue
          }
        }
      }

      // Email validation
      if (question.type === 'email' && answer) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(answer)) {
          newErrors[question.id] = 'Please enter a valid email address'
        }
      }

      // Number validation
      if (question.type === 'number' && answer !== undefined && answer !== '') {
        const num = Number(answer)
        if (isNaN(num)) {
          newErrors[question.id] = 'Please enter a valid number'
        } else {
          if (question.min !== null && question.min !== undefined && num < question.min) {
            newErrors[question.id] = `Minimum value is ${question.min}`
          }
          if (question.max !== null && question.max !== undefined && num > question.max) {
            newErrors[question.id] = `Maximum value is ${question.max}`
          }
        }
      }

      // Phone validation (country-specific digit length)
      if (question.type === 'phone' && answer?.number) {
        const result = validatePhoneNumber(answer.countryCode, answer.number)
        if (!result.valid) {
          newErrors[question.id] = result.message
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [form.questions, answers])

  // Handle form submission
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()

      if (!validate()) {
        // Scroll to first error
        const firstErrorId = Object.keys(errors)[0]
        if (firstErrorId) {
          const element = document.getElementById(`question-${firstErrorId}`)
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        return
      }

      if (onSubmit) {
        try {
          setSubmitError('')
          await onSubmit(answers)
          setSubmitted(true)
        } catch (error) {
          console.error('Submission error:', error)
          // Show user-friendly error from the backend (e.g. survey not active, ended, etc.)
          const message =
            error?.message || error?.data?.detail || 'Something went wrong. Please try again.'
          setSubmitError(message)
        }
      }
    },
    [answers, errors, onSubmit, validate]
  )

  // Show thank you message after submission
  if (submitted) {
    return (
      <div className="form-renderer submitted">
        <div className="thank-you">
          <div className="thank-you-icon">✓</div>
          <h2>Thank you!</h2>
          <p>Your response has been recorded.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`form-renderer ${textDirection === 'rtl' ? 'rtl' : 'ltr'}`} dir={textDirection}>
      <form onSubmit={handleSubmit}>
        {/* Brand Image */}
        {brandImageUrl && (
          <div className="form-brand">
            <img
              src={brandImageUrl}
              alt="Brand"
              className="form-brand-image"
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
          </div>
        )}

        {/* Form Header */}
        {(form.title || form.description) && (
          <div className="form-header">
            {form.title && <h1 className="form-title">{form.title}</h1>}
            {form.description && <p className="form-description">{form.description}</p>}
          </div>
        )}

        {/* Questions */}
        <div className="form-questions">
          {(form.questions || []).map((question, index) => {
            const isLayout = LAYOUT_TYPES.includes(question.type)
            const questionNum = isLayout
              ? null
              : (form.questions || []).slice(0, index).filter((q) => !LAYOUT_TYPES.includes(q.type))
                  .length + 1
            // Effective background: per-question overrides global
            const bgColor = question.backgroundColor || form.questionBackgroundColor || null
            return (
              <QuestionField
                key={question.id}
                question={question}
                index={index}
                questionNumber={questionNum}
                value={answers[question.id]}
                error={errors[question.id]}
                onChange={(value) => handleAnswerChange(question.id, value)}
                onBlur={handleFieldBlur}
                surveyId={surveyId}
                responseId={responseId}
                uploadToken={uploadToken}
                allowedResponseTypes={allowedResponseTypes}
                backgroundColor={bgColor}
              />
            )
          })}
        </div>

        {/* Submit Error Message */}
        {submitError && (
          <div className="form-submit-error">
            <span className="submit-error-icon">⚠</span>
            <span>{submitError}</span>
          </div>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  )
}
